import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Section } from "@/components/site/Section";
import { CTAButton, mailto } from "@/components/site/CTAButton";

const COUNTRY_ROWS: string[][] = [
  ["Germany", "None", "€350 – €700", "Highest later-year annuities; almost always validated"],
  ["France", "None", "€300 – €600", "Automatic effect; low validation cost"],
  ["United Kingdom", "None", "€300 – €600", "Outside the Unitary Patent — always a separate decision"],
  ["Netherlands", "None (English accepted)", "€350 – €700", "Common in the classic top four"],
  ["Italy", "Full translation", "€1,200 – €2,500", "Translation is the dominant cost"],
  ["Spain", "Full translation", "€1,200 – €2,500", "Translation is the dominant cost"],
  ["Poland", "Full translation", "€1,000 – €2,000", "Low annuities, high entry cost"],
  ["Switzerland / Liechtenstein", "None", "€250 – €500", "Not an EU state; never covered by the Unitary Patent"],
  ["Austria", "Claims only", "€400 – €800", "Moderate throughout"],
  ["Sweden", "Claims only", "€400 – €800", "Moderate throughout"],
  ["Turkey", "Full translation", "€1,200 – €2,500", "EPC state, outside the EU"],
  ["Unitary Patent (participating EU states)", "One translation during transition", "€1,000 – €1,800", "Single request, one renewal stream, no national agents"],
];

const FAQS = [
  {
    q: "What is European patent validation?",
    a: "After the EPO grants a European patent, it is not automatically enforceable anywhere. You must validate it in each contracting state where you want protection — or request unitary effect — within three months of the mention of grant.",
  },
  {
    q: "How much does it cost to validate a European patent?",
    a: "Typically €300 to €700 per country where no translation is required, and €1,000 to €2,500 where a full translation is. A classic five-country validation usually lands between €3,000 and €8,000 in one-off costs.",
  },
  {
    q: "Which countries require a translation?",
    a: "Italy, Spain, Poland and Turkey require a full translation of the specification. Austria and Sweden require the claims. Germany, France, the UK, the Netherlands and Switzerland require none if the patent is in English.",
  },
  {
    q: "Is the Unitary Patent cheaper than validation?",
    a: "Beyond roughly four or five countries, usually yes: one request, no national agents and a single renewal stream. For one or two markets, selective national validation is cheaper.",
  },
  {
    q: "What is the deadline to validate?",
    a: "Three months from the date the mention of grant is published in the European Patent Bulletin in most states. Miss it and the patent has no effect in that country, with limited and expensive restoration options.",
  },
];

export const Route = createFileRoute("/tools/european-patent-validation-costs")({
  head: () => ({
    meta: [
      { title: "European Patent Validation Costs by Country (2026)" },
      {
        name: "description",
        content:
          "What it costs a US company to validate a European patent country by country: translation requirements, per-country fee ranges and the 3-month deadline.",
      },
      {
        property: "og:title",
        content: "European Patent Validation Costs by Country (2026)",
      },
      { property: "og:image", content: "https://bauerip.com/og-us.jpg" },
      { name: "twitter:image", content: "https://bauerip.com/og-us.jpg" },
      {
        property: "og:description",
        content:
          "Per-country validation cost ranges, translation requirements and the Unitary Patent alternative — written for US patent owners.",
      },
      { property: "og:url", content: "https://bauerip.com/tools/european-patent-validation-costs" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://bauerip.com/tools/european-patent-validation-costs",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: ValidationCostsPage,
});

function ValidationCostsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        <Section
          eyebrow="After grant"
          title="European Patent Validation Costs by Country"
          titleAs="h1"
          intro="A European patent is granted centrally and enforced nationally. Within three months of grant you either validate it country by country or request unitary effect — and that single decision sets your cost base for the next twenty years."
        >
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">
                Indicative European patent validation costs and translation requirements by country
              </caption>
              <thead className="bg-secondary/50 text-foreground">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">Country</th>
                  <th scope="col" className="px-4 py-3 font-medium">Translation required</th>
                  <th scope="col" className="px-4 py-3 font-medium">One-off validation cost</th>
                  <th scope="col" className="px-4 py-3 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                {COUNTRY_ROWS.map((row) => (
                  <tr key={row[0]} className="border-t border-border">
                    {row.map((cell, i) => (
                      <td key={i} className={`px-4 py-3 ${i === 0 ? "text-foreground" : ""}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-6 max-w-3xl text-sm text-muted-foreground">
            Indicative ranges in euros, including local representation and translation, for planning
            purposes only. The actual cost depends on the length of the specification, the language
            pair and local attorney rates. Ask for a written estimate before budgeting.
          </p>
        </Section>

        <Section
          className="border-t border-border"
          eyebrow="How to decide"
          title="Three questions that settle the validation strategy"
        >
          <div className="grid gap-8 md:grid-cols-3">
            {[
              {
                t: "Where is the revenue?",
                d: "Validate where you sell, manufacture or licence. Coverage in a market you will never enforce in is a twenty-year bill for nothing.",
              },
              {
                t: "Where are the competitors?",
                d: "A patent is only useful where an infringer can be stopped. Map competitor manufacturing and import routes, not just your own sales.",
              },
              {
                t: "How many countries?",
                d: "Beyond four or five states the Unitary Patent usually beats classic validation on total cost — but it cannot be pruned later, and it never covers the UK, Switzerland, Norway or Turkey.",
              },
            ].map((item) => (
              <div key={item.t}>
                <h2 className="font-serif text-xl text-foreground">{item.t}</h2>
                <p className="mt-3 text-muted-foreground leading-relaxed">{item.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <CTAButton
              variant="accent"
              trackLabel="validation_costs_consultation"
              href={mailto({
                subject: "European patent validation estimate",
                body: "Hello Karel,\n\nI have a European patent approaching grant.\n\n— Application number:\n— Expected grant date:\n— Target countries:\n— Unitary Patent under consideration: yes / no\n",
              })}
            >
              Get a written validation estimate
            </CTAButton>
            <Link
              to="/tools/epo-renewal-fees"
              className="inline-flex items-center justify-center rounded-full border border-foreground/25 px-6 py-3 text-sm font-medium tracking-wide text-foreground transition-colors hover:border-foreground hover:bg-foreground/5"
            >
              Compare renewal fees →
            </Link>
          </div>
        </Section>

        <Section
          className="border-t border-border"
          eyebrow="Validation FAQ"
          title="Questions US patent owners ask"
        >
          <dl className="grid gap-8 md:grid-cols-2">
            {FAQS.map((f) => (
              <div key={f.q}>
                <dt className="font-serif text-xl text-foreground">{f.q}</dt>
                <dd className="mt-3 text-muted-foreground leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>

          <p className="mt-12 text-sm text-muted-foreground">
            Handled end to end by a registered EPO attorney:{" "}
            <Link
              to="/services/european-patent-attorney"
              className="underline underline-offset-4"
            >
              European patent prosecution services for US applicants
            </Link>
            .
          </p>
        </Section>
      </main>

      <SiteFooter />
    </div>
  );
}
